import React, { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { selectModalName } from 'redux/modal/ModalSlice';
import { selectSelectedWorkflow } from 'redux/runOrchestrations/RunOrchestrationsSlice';
import { MODAL_NAMES } from 'components/Modals/modal-list';
import { InputField, SelectField } from '@wk/components-react16';
import { IWorkflowProduct } from 'interfaces/runOrchestration/workflowRow';
import style from './CreateProductParams.module.scss'; // do not remove this line
import {
  FieldErrorsImpl,
  FieldValues,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form';
import { MAX_INTEGER_CSB } from 'utils/common-constants';
import { addToast } from 'redux/toast/toastSlice';

type ParameterType = 'text' | 'select';

interface Parameter {
  name: string;
  options?: string[];
  paramId: keyof IWorkflowProduct;
  required?: boolean;
  type: ParameterType;
}

interface ICreateProductParameters {
  clearErrors: UseFormClearErrors<FieldValues>;
  errors: Partial<FieldErrorsImpl>;
  register: UseFormRegister<FieldValues>;
  setError: UseFormSetError<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
}

export default function CreateProductParameters({
  register,
  setError,
  clearErrors,
  setValue,
  errors,
}: ICreateProductParameters) {
  const selectedOrchestration = useAppSelector(selectSelectedWorkflow);
  const modalName = useAppSelector(selectModalName);

  useEffect(() => {
    if (modalName === MODAL_NAMES.CREATE_PRODUCT) {
      setError('name', { message: 'This field is required', type: 'custom' });
    }
    if (modalName === MODAL_NAMES.EDIT_PRODUCT && selectedOrchestration) {
      for (const [key, value] of Object.entries(selectedOrchestration)) {
        if (value) {
          setValue(key as keyof IWorkflowProduct, value);
        }
      }
    }
  }, [modalName, selectedOrchestration, setError, setValue]);

  const handleSetError = useCallback(
    (name: string, error: { message: string; type: string }) => {
      setError(name, error);
    },
    [setError]
  );

  const handleClearErrors = useCallback(
    (name: string) => {
      clearErrors(name);
    },
    [clearErrors]
  );

  const parameters: Parameter[] = [
    { name: 'Product Name', paramId: 'name', required: true, type: 'text' },
    {
      name: 'Business Product',
      paramId: 'businessProduct',
      required: false,
      type: 'text',
    },
    {
      name: 'Throttle Rate',
      paramId: 'throttleRate',
      required: false,
      type: 'text',
    },
    {
      name: 'Cron Trigger',
      paramId: 'cronTrigger',
      required: false,
      type: 'text',
    },
    {
      name: 'Cron Timezone',
      paramId: 'cronTimezone',
      required: false,
      type: 'text',
    },
    {
      name: 'SFTP Dropzone',
      paramId: 'sftpDropzone',
      required: false,
      type: 'text',
    },
    {
      name: 'SFTP Private Key',
      paramId: 'sftpPrivateKey',
      required: false,
      type: 'text',
    },
    {
      name: 'FTP Dropzone',
      paramId: 'ftpDropzone',
      required: false,
      type: 'text',
    },
    {
      name: 'S3 Dropzone',
      paramId: 's3Dropzone',
      required: false,
      type: 'text',
    },
    {
      name: 'Notification Emails',
      paramId: 'notifyEmails',
      required: false,
      type: 'text',
    },
    {
      name: 'Event Params Mapping',
      paramId: 'eventParametersMapping',
      required: false,
      type: 'text',
    },
    {
      name: 'Reporting Index',
      paramId: 'reportingIndex',
      required: false,
      type: 'text',
    },
    {
      name: 'UnpackProduct',
      options: ['false', 'true'],
      paramId: 'unpackInput',
      required: false,
      type: 'select',
    },
  ];

  return (
    <div data-testid="CreateProductParams">
      {parameters.map((parameter) => (
        <MemoizedFormField
          key={parameter.paramId}
          param={parameter}
          register={register}
          setValue={setValue}
          errors={errors}
          setError={handleSetError}
          clearErrors={handleClearErrors}
          modalName={modalName}
          selectedOrchestration={selectedOrchestration}
        />
      ))}
    </div>
  );
}

const MemoizedFormField = React.memo(FormField);
function FormField({
  param,
  register,
  setValue,
  errors,
  setError,
  clearErrors,
  modalName,
  selectedOrchestration,
}: any) {
  const dispatch = useAppDispatch();

  const validateName = (
    value: string,
    errors: any,
    setError: any,
    clearErrors: any
  ) => {
    if (!value) {
      if (!errors.name) {
        setError('name', { message: 'This field is required', type: 'custom' });
      }
    } else if (errors.name) {
      clearErrors('name');
    }
  };

  const validateThrottleRate = (
    value: string,
    errors: any,
    setError: any,
    clearErrors: any
  ) => {
    const isValid =
      value === 'null' ||
      value === '' ||
      (!isNaN(+value) &&
        Number.parseInt(value) >= 0 &&
        Number.parseInt(value) <= MAX_INTEGER_CSB);
    if (isValid && Number.parseInt(value) === 0) {
      dispatch(
        addToast({
          additionalData: {
            text: 'All orchestration instances for a product variant with a "0" throttle rate value will be marked as "Pending"',
          },
          closeTimeout: 7000,
          id: `throtttleRate-${(Math.random() + 1).toString()}`,
          status: 'info',
          type: 'informationToast',
        })
      );
    }
    if (isValid && errors.throttleRate) {
      clearErrors('throttleRate');
    } else if (!isValid && !errors.throttleRate) {
      setError('throttleRate', {
        message: 'Use only positive numbers or null',
        type: 'custom',
      });
    }
  };

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { id, value } = event.target;
      setValue(id, value);

      if (id === 'name') {
        validateName(value, errors, setError, clearErrors);
      } else if (id === 'throttleRate') {
        validateThrottleRate(value, errors, setError, clearErrors);
      }
    },
    [setValue, setError, clearErrors, errors]
  );
  const { required } = param;
  const indicator = required ? 'required' : 'none';
  if (param.type === 'select') {
    return (
      <SelectField
        label={param.name}
        labelFor={param.paramId}
        indicator={indicator}
      >
        <select
          id={param.paramId}
          data-testid={`Select-${param.paramId}`}
          {...register(param.paramId)}
          onChange={handleChange}
          defaultValue={
            modalName === MODAL_NAMES.EDIT_PRODUCT
              ? selectedOrchestration?.[
                  param.paramId as keyof IWorkflowProduct
                ]?.toString()
              : ''
          }
          required={required}
        >
          <option value="false">No</option>
          <option value="true">Yes</option>
        </select>
      </SelectField>
    );
  }

  return (
    <InputField
      label={param.name}
      labelFor={param.paramId}
      errors={
        errors[param.paramId]
          ? [{ label: errors[param.paramId].message, labelId: param.paramId }]
          : undefined
      }
      indicator={indicator}
    >
      <input
        type={param.type}
        id={param.paramId}
        data-testid={`Input-${param.paramId}`}
        {...register(param.paramId)}
        onChange={handleChange}
        defaultValue={
          modalName === MODAL_NAMES.EDIT_PRODUCT
            ? selectedOrchestration?.[
                param.paramId as keyof IWorkflowProduct
              ]?.toString()
            : undefined
        }
        required={required}
      />
    </InputField>
  );
}
